/**
 * CombinedForm - Main v2 Entry Point
 * ===================================
 * Unified FIS/USSA workflow with event-first approach.
 */

import React, { useState, useEffect } from 'react';
import './styles/CombinedForm.css';

// Components
import EventPicker from './components/EventPicker';
import RacePicker from './components/RacePicker';
import FileUploader from './components/FileUploader';
import CategoryPicker from './components/CategoryPicker';
import HeaderSummary from './components/HeaderSummary';
import JuryForm from './components/JuryForm';
import DataWorkflow from './components/DataWorkflow';
import CleanPreview from './components/CleanPreview';

// App header (replaces Zone4Header + NavBar + old sub-header)
import AppHeader from './components/AppHeader';
import FisPointsModal from './components/FisPointsModal';
import UssaPointsModal from './components/UssaPointsModal';

// Hooks
import { useLocalStorage } from './hooks/useLocalStorage';

// Utils
import { extractCategoryData } from './utils/categoryParser';
import { cleanUssaData } from './utils/ussaCleanup';
import { cleanFisData } from './utils/fisCleanup';
import { detectMissingFields, applyColumnMappings, normalizeRacers } from './utils/columnNormalizer';

// Column Mapper Modal
import ColumnMapperModal from './components/ColumnMapperModal';

// Data
import listPeriods2026 from './data/listPeriods2026.json';
import packageJson from '../../package.json';

// Logos
import fisLogo from '../logos/fis.svg';

// Helper functions for list period calculation
function calculateFisListNumber(raceDate) {
  if (!raceDate) return '';
  const date = new Date(raceDate);
  const fisList = listPeriods2026.fis.lists.find((period) => {
    const from = new Date(period.validFrom);
    const to = new Date(period.validTo);
    return date >= from && date <= to;
  });
  return fisList ? fisList.code : '';
}

function calculateUssaListCode(raceDate) {
  if (!raceDate) return '';
  const date = new Date(raceDate);
  const ussaList = listPeriods2026.ussa.lists.find((period) => {
    const from = new Date(period.validFrom);
    const to = new Date(period.validTo);
    return date >= from && date <= to;
  });
  return ussaList ? ussaList.listNumber : '';
}

function csvEscape(value) {
  const str = value === null || value === undefined ? '' : String(value);
  if (str.includes('"') || str.includes(',') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsv(fields, rows = []) {
  const headerLine = fields.map(csvEscape).join(',');
  if (!rows.length) return `${headerLine}\n`;
  const dataLines = rows.map((row) =>
    fields.map((field) => csvEscape(row[field] ?? '')).join(',')
  );
  return `${headerLine}\n${dataLines.join('\n')}\n`;
}

function downloadTemplateCsv(templateType) {
  let filename = '';
  let csvData = '';

  if (templateType === 'individual') {
    const headers = ['rank', 'time', 'status', 'firstname', 'lastname', 'Racer #', 'FIScode', 'USSAcode', 'FISpoints', 'NRLpoints', 'YOB', 'Country', 'Race Points'];
    filename = 'Individual_Template.csv';
    csvData = buildCsv(headers);
  } else if (templateType === 'sprint-final') {
    const headers = ['rank', 'time', 'status', 'firstname', 'lastname', 'Racer #', 'FIScode', 'USSAcode', 'FISpoints', 'NRLpoints', 'YOB', 'Country', 'Race Points', 'Highest Level'];
    const rows = Array.from({ length: 30 }, (_, index) => {
      if (index <= 5) return { 'Highest Level': 'final' };
      if (index <= 11) return { 'Highest Level': 'semifinal' };
      return { 'Highest Level': 'quarterfinal' };
    });
    filename = 'Sprint_Final_Template.csv';
    csvData = buildCsv(headers, rows);
  } else if (templateType === 'team') {
    const headers = ['rank', 'bib', 'time', 'status', 'teamname', 'nation', 'sex', 'FIScode'];
    filename = 'Team_Template.csv';
    csvData = buildCsv(headers);
  } else {
    return;
  }

  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function CombinedForm() {
  // ==========================================================================
  // STATE - Persisted in localStorage
  // ==========================================================================
  
  // Race type: 'ussa' (US race with USSA event lookup) or 'fis-only' (Non-US FIS race)
  const [raceMode, setRaceMode] = useLocalStorage('v2_raceMode', null);
  
  // Event & Race selection (persists across sessions)
  const [selectedEvent, setSelectedEvent] = useLocalStorage('v2_selectedEvent', null);
  const [selectedRace, setSelectedRace] = useLocalStorage('v2_selectedRace', null);
  
  // Original CSV data (persists until new upload)
  const [originalCsv, setOriginalCsv] = useLocalStorage('v2_originalCsv', {
    fileName: '',
    rawRows: [],      // Raw CSV rows as arrays (for multi-category extraction)
    headers: [],
    categories: [],
    categoryMeta: [], // { name, startIndex } for each category
    isMultiCategory: false,
    uploadedAt: null,
  });
  
  // Category progress tracking
  const [categoryProgress, setCategoryProgress] = useLocalStorage('v2_categoryProgress', {});
  // Format: { "10k Men": { fisExported: true, ussaExported: false }, ... }
  
  // Header info (shared fields)
  const [headerInfo, setHeaderInfo] = useLocalStorage('v2_headerInfo', {
    // General Settings
    sector: 'CC',           // Discipline Type: CC, PCC, PSB, PAL
    tempunit: 'C',          // Temperature unit
    longunit: 'm',          // Length unit
    speedunit: 'kmh',       // Speed unit
    windunit: 'ms',         // Wind unit
    timingBy: 'Zone4Systems',
    softWareCompany: 'Zone4Systems',
    dataProcessingBy: 'Zone4Systems',
    
    // Event Info
    eventName: '',
    clubName: '',
    location: '',
    state: '',
    nation: 'USA',
    category: 'FIS',        // FIS category (FIS Race, SuperTour, etc.)
    resultType: 'Official', // Result type
    
    // Race Info
    discipline: '',         // SP or DI
    format: '',             // individualDistance, sprintQualification, etc.
    eventType: '',          // MS, IS, Qual, Final, Pur
    raceDate: new Date().toISOString().slice(0, 10),
    technique: '',          // FR or CL
    distance: '',
    
    // Course Info
    homologation: '',
    lapLength: '',
    lapNumber: '',
    coordinator: '',
    tc: '',                 // Total Climb
    hd: '',                 // Height Difference
    mc: '',                 // Maximum Climb
    
    // File/Codex Info
    sex: '',                // M, W, or A (team)
    fisCodex: '',
    ussaCode: '',
    startTime: '10:00',
    
    // Auto-calculated
    fisListNumber: '',
    ussaListCode: '',
  });
  
  // Jury (shared) - must be an array for JuryForm
  const [jury, setJury] = useLocalStorage('v2_jury', []);

  // ==========================================================================
  // STATE - Session only (not persisted)
  // ==========================================================================
  
  // Currently selected category
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // SEPARATE working data for FIS and USSA (key fix for shared data issue)
  const [fisRacers, setFisRacers] = useState([]);
  const [ussaRacers, setUssaRacers] = useState([]);
  
  // Cleanup preview state
  const [cleanPreviewOpen, setCleanPreviewOpen] = useState(false);
  const [cleanPreviewType, setCleanPreviewType] = useState(null); // 'fis' or 'ussa'
  const [cleanPreviewResult, setCleanPreviewResult] = useState(null);
  
  // Column mapper state (for when CSV has unrecognized column names)
  const [columnMapperOpen, setColumnMapperOpen] = useState(false);
  const [columnMapperData, setColumnMapperData] = useState({
    missingFields: [],
    availableColumns: [],
    pendingData: [], // Data waiting for mapping before proceeding
  });
  // Per-file manual column mappings (canonicalField -> sourceColumn)
  const [columnMappings, setColumnMappings] = useState({});
  
  // UI state
  const [activeSection, setActiveSection] = useState('event'); // event, header, jury, file, fis, ussa
  const [error, setError] = useState(null);
  
  // FIS/USSA workflow state
  const [fisWorkflowState, setFisWorkflowState] = useState({
    cleaned: false,
    pointsCalculated: false,
    exported: false,
    penalty: '',
    fFactor: 800,
  });
  const [ussaWorkflowState, setUssaWorkflowState] = useState({
    cleaned: false,
    pointsCalculated: false,
    exported: false,
    penalty: '',
    fFactor: 800,
  });

  // ==========================================================================
  // DERIVED STATE
  // ==========================================================================
  
  // Determine available workflows based on codex and race mode
  const hasFisCodex = !!headerInfo.fisCodex;
  const hasUssaCode = !!headerInfo.ussaCode;
  // FIS workflow is enabled if: FIS codex specified OR in fis-only mode
  const fisWorkflowEnabled = hasFisCodex || raceMode === 'fis-only';
  // USSA workflow is enabled if: USSA code specified (only for USSA races)
  const ussaWorkflowEnabled = hasUssaCode;
  
  // Current season
  const currentSeason = 2026;
  
  // Navigation state - new order: Event → Header → Jury → File → Data → Export
  const canAccessHeader = raceMode === 'fis-only' || selectedRace; // After event/race selected
  const canAccessJury = raceMode === 'fis-only' || selectedRace;   // After event/race selected
  const canAccessFile = raceMode === 'fis-only' || selectedRace;   // After event/race selected
  const canAccessData = !!originalCsv.fileName;                     // After file uploaded

  // ==========================================================================
  // EFFECTS
  // ==========================================================================
  
  // Auto-detect list numbers from race date
  useEffect(() => {
    if (headerInfo.raceDate) {
      const fisInfo = listPeriods2026.fis;
      const ussaInfo = listPeriods2026.ussa;
      
      const raceDate = headerInfo.raceDate;
      
      // Find FIS list
      const fisList = fisInfo.lists.find(
        (l) => raceDate >= l.validFrom && raceDate <= l.validTo
      );
      if (fisList && !headerInfo.fisListNumber) {
        setHeaderInfo((prev) => ({ ...prev, fisListNumber: fisList.code }));
      }
      
      // Find USSA list
      const ussaList = ussaInfo.lists.find(
        (l) => raceDate >= l.validFrom && raceDate <= l.validTo
      );
      if (ussaList && !headerInfo.ussaListCode) {
        setHeaderInfo((prev) => ({ ...prev, ussaListCode: ussaList.code }));
      }
    }
  }, [headerInfo.raceDate]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  
  // Select race mode
  const handleModeSelect = (mode) => {
    setRaceMode(mode);
    if (mode === 'fis-only') {
      const today = new Date().toISOString().slice(0, 10);
      // Non-USA flow should always start clean from USSA-specific metadata.
      setSelectedEvent(null);
      setSelectedRace(null);
      // Skip event selection, go to header (new flow: Event → Header → Jury → File)
      // Default nation to CAN for non-USA FIS races
      setHeaderInfo((prev) => ({
        ...prev,
        nation: 'CAN',
        state: '',
        clubName: '',
        ussaCode: '',
        usedNRLList: '',
        ussaListCode: '',
        eventType: '',
        raceDate: today,
        fisListNumber: '',
        usedFISList: '',
      }));
      setActiveSection('header');
    }
  };
  
  // When event is selected from EventPicker
  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setSelectedRace(null); // Clear race selection
    
    // Update header with event info (only if event is not null)
    if (event) {
      setHeaderInfo((prev) => ({
        ...prev,
        eventName: event.name || '',
        place: event.location || '',  // Map location to place field
        state: event.state || '',
        nation: 'USA',  // USSA events are always in USA
      }));
    }
    
    // Stay on event section to let user select a race
  };
  
  // When race is selected from RacePicker
  const handleRaceSelect = (race) => {
    setSelectedRace(race);
    
    // Check if this race was previously processed with a CSV category
    const raceCode = race.code || race.fisCodex || '';
    const raceProgress = categoryProgress[raceCode];
    
    if (raceProgress?.categoryName && originalCsv.categories.includes(raceProgress.categoryName)) {
      // Re-select the previously used category
      console.log(`[RaceSelect] Re-selecting category "${raceProgress.categoryName}" for race ${raceCode}`);
      setSelectedCategory(raceProgress.categoryName);
      
      // Load the category data
      const catIndex = originalCsv.categories.indexOf(raceProgress.categoryName);
      if (catIndex >= 0) {
        const meta = originalCsv.categoryMeta[catIndex];
        if (meta) {
          const nextMeta = originalCsv.categoryMeta[catIndex + 1];
          const endIndex = nextMeta ? nextMeta.startIndex : originalCsv.rawRows.length;
          const categoryData = originalCsv.rawRows.slice(meta.startIndex, endIndex);
          
          // Set racers for both tabs
          setFisRacers(categoryData);
          setUssaRacers(categoryData);
          
          // Reset workflow states for the new category
          setFisWorkflowState({ cleaned: false, pointsCalculated: false, exported: raceProgress.fisExported || false });
          setUssaWorkflowState({ cleaned: false, pointsCalculated: false, exported: raceProgress.ussaExported || false });
        }
      }
    } else {
      // New race - use race code for progress tracking
      setSelectedCategory(raceCode);
    }
    
    // Map API values to header field values
    const mapGender = (g) => {
      if (!g) return '';
      if (g.toLowerCase() === 'male') return 'M';
      if (g.toLowerCase() === 'female') return 'W';
      return g;
    };
    
    const mapDiscipline = (d) => {
      if (!d) return '';
      const lower = d.toLowerCase();
      if (lower === 'sprint') return 'SP';
      if (lower === 'distance') return 'DI';
      if (lower === 'team relay') return 'TE';
      if (lower === 'team sprint') return 'Tsp';
      return d;
    };
    
    const mapTechnique = (t) => {
      if (!t) return '';
      const trimmed = t.trim().toUpperCase();
      if (trimmed === 'FR' || trimmed.startsWith('FR') || trimmed === 'FREESTYLE' || trimmed === 'F') return 'FR';
      if (trimmed === 'CL' || trimmed.startsWith('CL') || trimmed === 'CLASSIC' || trimmed === 'C') return 'CL';
      return trimmed;
    };
    
    // Map race type to eventType and format
    // Types from USSA API: SPQ, SPF, MST, IS, PUR, etc.
    // Map USSA API race type → eventType (for USSA XML) + format (for FIS XML)
    // Format dropdown is constrained to valid XML values: individualDistance, sprintQualification, sprintFinal, team, teamSprint
    const mapRaceType = (raceType) => {
      if (!raceType) return { eventType: '', format: '' };
      const type = raceType.toUpperCase();
      
      // Sprint types
      if (type === 'SPQ' || type === 'SPRINT QUALIFICATION') {
        return { eventType: 'Qual', format: 'sprintQualification' };
      }
      if (type === 'SPF' || type === 'SPRINT FINAL' || type === 'SPRINT FINALS') {
        return { eventType: 'Final', format: 'sprintFinal' };
      }
      
      // Distance types — all map to 'individualDistance' format (the only valid non-sprint format)
      if (type === 'IS' || type === 'INTERVAL START' || type === 'INDIVIDUAL START') {
        return { eventType: 'IS', format: 'individualDistance' };
      }
      if (type === 'MST' || type === 'MS' || type === 'MASS START') {
        return { eventType: 'MS', format: 'individualDistance' };
      }
      if (type === 'PUR' || type === 'PURSUIT') {
        return { eventType: 'Pur', format: 'individualDistance' };
      }
      if (type === 'SKI' || type === 'SKIATHLON') {
        return { eventType: 'Ski', format: 'individualDistance' };
      }
      
      // Team types
      if (type === 'REL' || type === 'RELAY' || type === 'TEAM RELAY') {
        return { eventType: 'Rel', format: 'team' };
      }
      if (type === 'TSP' || type === 'TEAM SPRINT') {
        return { eventType: 'Tsp', format: 'teamSprint' };
      }
      
      return { eventType: type, format: '' };
    };
    
    const raceDate = race.date ? race.date.slice(0, 10) : '';
    
    // Get race type from various possible fields
    const raceType = race.crossCountry?.type || race.type || race.eventType || '';
    const { eventType, format } = mapRaceType(raceType);
    console.log(`[RaceSelect] raceType="${raceType}" → eventType="${eventType}", format="${format}"`);
    
    // Calculate which list periods this race falls into
    const fisListNumber = raceDate ? calculateFisListNumber(raceDate) : '';
    const ussaListCode = raceDate ? calculateUssaListCode(raceDate) : '';
    
    // Update header with race info - always auto-fill list numbers from race date
    setHeaderInfo((prev) => ({
      ...prev,
      fisCodex: race.fisCodex || '',
      ussaCode: race.code || '',
      sex: mapGender(race.gender),
      discipline: mapDiscipline(race.discipline),
      technique: mapTechnique(race.technique),
      distance: race.crossCountry?.distance ? `${race.crossCountry.distance}km` : '',
      raceDate: raceDate || prev.raceDate,
      // Auto-fill event type and format from race type
      eventType: eventType || prev.eventType,
      format: format || prev.format,
      // Auto-fill list numbers based on race date
      usedFISList: fisListNumber || prev.usedFISList,
      usedNRLList: ussaListCode || prev.usedNRLList,
      fisListNumber: fisListNumber,
      ussaListCode: ussaListCode,
    }));
    
    // Advance to header section after race is selected (new flow: Event → Header → Jury → File)
    setActiveSection('header');
  };
  
  // When file is uploaded
  const handleFileUpload = (fileData) => {
    console.log('File uploaded:', fileData);
    
    setOriginalCsv({
      fileName: fileData.fileName,
      rawRows: fileData.rawRows || [],
      headers: fileData.headers || [],
      categories: fileData.categories || [],
      categoryMeta: fileData.categoryMeta || [],
      isMultiCategory: fileData.isMultiCategory || false,
      data: fileData.data || [], // For single-category files
      uploadedAt: new Date().toISOString(),
    });
    
    // Reset category progress for new file
    setCategoryProgress({});
    setSelectedCategory('');
    setFisRacers([]);
    setUssaRacers([]);
    setColumnMappings({});
    
    // If no categories (single category file), check for missing columns
    if (!fileData.categories || fileData.categories.length === 0) {
      const racerData = fileData.data || [];
      const mappedRacerData = applyColumnMappings(racerData, {});
      
      // Check for missing required columns based on race type
      const { missingFields, availableColumns } = detectMissingFields(mappedRacerData, {
        isFIS: hasFisCodex || raceMode === 'fis-only',
        isUSSA: hasUssaCode,
      });
      
      if (missingFields.length > 0) {
        // Show column mapper modal
        console.log('Missing fields detected (single file):', missingFields);
        console.log('Available columns:', availableColumns);
        setColumnMapperData({
          missingFields,
          availableColumns,
          pendingData: racerData,
        });
        setColumnMapperOpen(true);
        return; // Wait for user to map columns
      }
      
      // No missing fields - normalize column names and proceed
      const normalized = normalizeRacers(mappedRacerData);
      setFisRacers([...normalized]);
      setUssaRacers([...normalized]);
    }
  };
  
  // When category is selected
  const handleCategorySelect = (categoryName) => {
    setSelectedCategory(categoryName);
    
    // Find the category metadata
    const catMeta = originalCsv.categoryMeta?.find(c => c.name === categoryName);
    let categoryData = [];
    
    if (catMeta && originalCsv.rawRows) {
      // Extract data for this category using the parser
      categoryData = extractCategoryData(originalCsv.rawRows, catMeta.startIndex);
      console.log('Extracted category data:', categoryName, categoryData.length, 'rows');
    } else {
      // Fallback: filter by category field in data
      categoryData = (originalCsv.data || []).filter(
        (row) => row.category === categoryName || row.Category === categoryName
      );
    }
    const mappedCategoryData = applyColumnMappings(categoryData, columnMappings);
    
    // Check for missing required columns based on race type
    const { missingFields, availableColumns } = detectMissingFields(mappedCategoryData, {
      isFIS: hasFisCodex || raceMode === 'fis-only',
      isUSSA: hasUssaCode,
    });
    
    if (missingFields.length > 0) {
      // Show column mapper modal
      console.log('Missing fields detected:', missingFields);
      console.log('Available columns:', availableColumns);
      setColumnMapperData({
        missingFields,
        availableColumns,
        pendingData: categoryData,
      });
      setColumnMapperOpen(true);
      return; // Wait for user to map columns before proceeding
    }
    
    // No missing fields, proceed normally
    finalizeCategoryLoad(mappedCategoryData);
  };
  
  // Helper to finalize category data load after any column mapping
  const finalizeCategoryLoad = (categoryData) => {
    // Normalize column names ONCE at entry - all downstream code uses canonical names
    const normalized = normalizeRacers(categoryData);
    // Initialize BOTH FIS and USSA with SEPARATE copies
    setFisRacers([...normalized]);
    setUssaRacers([...normalized]);
    
    // Navigate to next appropriate tab: FIS if visible, otherwise USSA
    if (hasFisCodex || raceMode === 'fis-only') {
      setActiveSection('fis');
    } else if (hasUssaCode) {
      setActiveSection('ussa');
    }
    
    // Reset workflow states for new category
    setFisWorkflowState({ cleaned: false, pointsCalculated: false, exported: false, penalty: '', fFactor: 800 });
    setUssaWorkflowState({ cleaned: false, pointsCalculated: false, exported: false, penalty: '', fFactor: 800 });
  };
  
  // Handle column mapping confirmation from modal
  const handleColumnMappingConfirm = (mappings) => {
    console.log('User column mappings:', mappings);
    const mergedMappings = { ...columnMappings, ...mappings };
    setColumnMappings(mergedMappings);
    
    // Apply the mappings to the pending data
    const mappedData = applyColumnMappings(columnMapperData.pendingData, mergedMappings);
    
    // Close modal
    setColumnMapperOpen(false);
    setColumnMapperData({ missingFields: [], availableColumns: [], pendingData: [] });
    
    // For multi-category files (category is already selected), finalize with navigation
    // For single-category files, just set the racers without navigation
    if (selectedCategory || (originalCsv.categories && originalCsv.categories.length > 0)) {
      finalizeCategoryLoad(mappedData);
    } else {
      // Single-category file - normalize and set racer data, stay on file section
      const normalized = normalizeRacers(mappedData);
      setFisRacers([...normalized]);
      setUssaRacers([...normalized]);
    }
  };
  
  // Handle column mapping cancellation
  const handleColumnMappingCancel = () => {
    setColumnMapperOpen(false);
    setColumnMapperData({ missingFields: [], availableColumns: [], pendingData: [] });
    // Reset category selection since user cancelled
    setSelectedCategory('');
  };

  // ==========================================================================
  // DATA WORKFLOW HANDLERS
  // ==========================================================================

  // Handle Clean Data (FIS or USSA)
  // Now shows preview dialog instead of immediately applying changes
  const handleCleanData = async (type) => {
    // Get the appropriate racer data for this type
    const racersToClean = type === 'fis' ? fisRacers : ussaRacers;
    console.log(`Cleaning ${type} data...`, racersToClean.length, 'racers');
    
    if (!racersToClean || racersToClean.length === 0) {
      console.warn('No racers to clean');
      return { success: false, error: 'No racers to clean' };
    }

    try {
      let result;
      
      if (type === 'ussa') {
        // Call USSA cleanup - USSA API uses 'sprint' or 'distance'
        // Also pass FIS list number for FIS points lookup (used in penalty calculation fallback)
        const fisListForCleanup = headerInfo.usedFISList || headerInfo.fisListNumber;
        console.log('[CombinedForm] USSA cleanup - FIS list values:', {
          usedFISList: headerInfo.usedFISList,
          fisListNumber: headerInfo.fisListNumber,
          using: fisListForCleanup,
        });
        result = await cleanUssaData(racersToClean, {
          discipline: headerInfo.discipline === 'SP' ? 'sprint' : 'distance',
          listNumber: headerInfo.ussaListCode || headerInfo.usedNRLList,
          fisListNumber: fisListForCleanup,
          sex: headerInfo.sex || 'M',
          raceDate: headerInfo.raceDate, // For age category calculation
          onProgress: (msg) => console.log('[USSA Cleanup]', msg),
        });
      } else if (type === 'fis') {
        // Call FIS cleanup - FIS API uses 'DI' or 'SP' for discipline
        result = await cleanFisData(racersToClean, {
          discipline: headerInfo.discipline || 'DI', // Already 'DI' or 'SP' from header
          listNumber: headerInfo.fisListNumber || headerInfo.usedFISList,
          onProgress: (msg) => console.log('[FIS Cleanup]', msg),
        });
      } else {
        return { success: false, error: 'Unknown cleanup type' };
      }
      
      console.log(`${type.toUpperCase()} Cleanup result:`, {
        validRacers: result.validRacers?.length,
        removedRacers: result.removedRacers?.length,
        changes: result.changes?.length,
      });
      
      // Show preview instead of immediately applying
      setCleanPreviewType(type);
      setCleanPreviewResult(result);
      setCleanPreviewOpen(true);
      
      return { success: true, ...result, showingPreview: true };
      
    } catch (err) {
      console.error(`Error during ${type} cleanup:`, err);
      return { success: false, error: err.message };
    }
  };
  
  // Handle accepting cleanup changes from preview
  const handleAcceptCleanup = (finalRacers) => {
    const type = cleanPreviewType;
    console.log(`Accepting ${type} cleanup with ${finalRacers.length} racers`);
    
    if (type === 'fis') {
      setFisRacers(finalRacers);
      setFisWorkflowState(prev => ({ ...prev, cleaned: true }));
      
      // IMPORTANT: Merge FIS-enriched data into USSA racers
      // This ensures USSA cleanup has access to FIS points, names, nation, YOB
      // but keeps all USSA racers (doesn't remove non-FIS racers)
      mergeEnrichedDataToUssa(finalRacers);
      
    } else if (type === 'ussa') {
      setUssaRacers(finalRacers);
      setUssaWorkflowState(prev => ({ ...prev, cleaned: true }));
    }
    
    // Close preview
    setCleanPreviewOpen(false);
    setCleanPreviewResult(null);
    setCleanPreviewType(null);
  };
  
  // Merge FIS-enriched data into USSA racers (after FIS cleanup)
  // This copies FIS points, names, nation, YOB from FIS cleanup results
  // but keeps all USSA racers (non-FIS racers stay in USSA)
  const mergeEnrichedDataToUssa = (fisEnrichedRacers) => {
    console.log('[Merge] Merging FIS-enriched data into USSA racers');
    
    // Create lookup maps using canonical field names
    const fisDataByFisCode = new Map();
    const fisDataByUssaCode = new Map();
    const fisDataByName = new Map();
    
    fisEnrichedRacers.forEach(racer => {
      const fisCode = racer.fisCode;
      const ussaCode = racer.ussaCode;
      const nameKey = `${(racer.firstName || '').toLowerCase()}-${(racer.lastName || '').toLowerCase()}`;
      
      if (fisCode) fisDataByFisCode.set(String(fisCode).trim(), racer);
      if (ussaCode) fisDataByUssaCode.set(String(ussaCode).trim(), racer);
      if (nameKey && nameKey !== '-') fisDataByName.set(nameKey, racer);
    });
    
    setUssaRacers(prevUssaRacers => {
      return prevUssaRacers.map(ussaRacer => {
        // Try to find matching FIS-enriched data using canonical names
        const fisCode = ussaRacer.fisCode;
        const ussaCode = ussaRacer.ussaCode;
        const nameKey = `${(ussaRacer.firstName || '').toLowerCase()}-${(ussaRacer.lastName || '').toLowerCase()}`;
        
        let fisMatch = null;
        if (fisCode) fisMatch = fisDataByFisCode.get(String(fisCode).trim());
        if (!fisMatch && ussaCode) fisMatch = fisDataByUssaCode.get(String(ussaCode).trim());
        if (!fisMatch && nameKey && nameKey !== '-') fisMatch = fisDataByName.get(nameKey);
        
        if (!fisMatch) {
          return ussaRacer;
        }
        
        // Merge enriched data from FIS cleanup — canonical names only
        const merged = { ...ussaRacer };
        
        if (fisMatch.fisPoints !== undefined && fisMatch.fisPoints !== null) {
          merged.fisPoints = fisMatch.fisPoints;
        }
        if (fisMatch.firstName) {
          merged.firstName = fisMatch.firstName;
        }
        if (fisMatch.lastName) {
          merged.lastName = fisMatch.lastName;
        }
        if (fisMatch.country) {
          merged.country = fisMatch.country;
        }
        if (fisMatch.yob) {
          merged.yob = fisMatch.yob;
        }
        if (fisMatch.fisCode && !fisCode) {
          merged.fisCode = fisMatch.fisCode;
        }
        
        return merged;
      });
    });
    
    console.log('[Merge] FIS data merged into USSA racers');
  };
  
  // Handle declining cleanup changes
  const handleDeclineCleanup = () => {
    console.log('Declining cleanup changes');
    setCleanPreviewOpen(false);
    setCleanPreviewResult(null);
    setCleanPreviewType(null);
  };

  // Handle Calculate Points
  const handleCalculatePoints = async (type, fFactor) => {
    console.log(`Calculating ${type} points with F-Factor:`, fFactor);
    // TODO: Integrate with actual penalty calculation
    // For now, simulate the calculation
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Simulate penalty calculation
    const penalty = (Math.random() * 50 + 10).toFixed(2);
    
    // Update the appropriate racer set with race points
    const updateRacersWithPoints = (racers) => {
      return racers.map((r, idx) => ({
        ...r,
        racePoints: (parseFloat(penalty) + idx * 1.5).toFixed(2),
      }));
    };
    
    if (type === 'fis') {
      setFisRacers(prev => updateRacersWithPoints(prev));
    } else if (type === 'ussa') {
      setUssaRacers(prev => updateRacersWithPoints(prev));
    }
    
    return { penalty, fFactor };
  };

  // Handle Download XML
  const handleDownloadXML = async (type) => {
    console.log(`Generating ${type} XML...`);
    
    // Import XML export functions
    const { exportFisXML, exportUssaXML } = await import('./utils/xmlExport');
    
    try {
      const racers = type === 'fis' ? fisRacers : ussaRacers;
      const workflowState = type === 'fis' ? fisWorkflowState : ussaWorkflowState;
      
      if (!racers || racers.length === 0) {
        throw new Error('No racers to export');
      }
      
      let filename;
      if (type === 'fis') {
        filename = await exportFisXML(headerInfo, racers, jury, workflowState);
      } else {
        filename = await exportUssaXML(headerInfo, racers, jury, workflowState);
      }
      
      console.log(`${type.toUpperCase()} XML downloaded:`, filename);
      
      // Update category progress (for file category and race code)
      setCategoryProgress(prev => {
        const updated = { ...prev };
        const raceCode = headerInfo.ussaCode || headerInfo.fisCodex;
        
        // Track by file category name
        if (selectedCategory) {
          updated[selectedCategory] = {
            ...updated[selectedCategory],
            [`${type}Exported`]: true,
            // Link to race code if available
            ...(raceCode && { raceCode }),
          };
        }
        
        // Also track by race code (for RacePicker status)
        // Store the category name so we can auto-select it when re-clicking the race
        if (raceCode) {
          updated[raceCode] = {
            ...updated[raceCode],
            [`${type}Exported`]: true,
            // Link back to the CSV category
            ...(selectedCategory && { categoryName: selectedCategory }),
          };
        }
        
        return updated;
      });
      
      return { success: true, filename };
    } catch (err) {
      console.error(`Error generating ${type} XML:`, err);
      alert(`Error generating ${type.toUpperCase()} XML: ${err.message}`);
      return { success: false, error: err.message };
    }
  };
  
  // Clear all and start fresh
  const handleClearAll = () => {
    if (window.confirm('Clear all data and start fresh?')) {
      setRaceMode(null);
      setSelectedEvent(null);
      setSelectedRace(null);
      setOriginalCsv({
        fileName: '',
        rawRows: [],
        headers: [],
        categories: [],
        categoryMeta: [],
        isMultiCategory: false,
        data: [],
        uploadedAt: null,
      });
      setCategoryProgress({});
      setHeaderInfo({
        // General Settings - keep user preferences
        sector: 'CC',
        tempunit: 'C',
        longunit: 'm',
        speedunit: 'kmh',
        windunit: 'ms',
        timingBy: 'Zone4Systems',
        softWareCompany: 'Zone4Systems',
        dataProcessingBy: 'Zone4Systems',
        // Event Info - clear
        eventName: '',
        clubName: '',
        location: '',
        state: '',
        nation: 'USA',
        category: 'FIS',
        resultType: 'Official',
        // Race Info - clear
        discipline: '',
        format: '',
        eventType: '',
        raceDate: new Date().toISOString().slice(0, 10),
        technique: '',
        distance: '',
        // Course Info - clear
        homologation: '',
        lapLength: '',
        lapNumber: '',
        coordinator: '',
        tc: '',
        hd: '',
        mc: '',
        // File/Codex Info - clear
        sex: '',
        fisCodex: '',
        ussaCode: '',
        startTime: '10:00',
        // Auto-calculated
        fisListNumber: '',
        ussaListCode: '',
      });
      setJury([]);
      setSelectedCategory('');
      // Reset separate FIS/USSA data
      setFisRacers([]);
      setUssaRacers([]);
      setActiveSection('event');
      // Reset workflow states
      setFisWorkflowState({ cleaned: false, pointsCalculated: false, exported: false, penalty: '', fFactor: 800 });
      setUssaWorkflowState({ cleaned: false, pointsCalculated: false, exported: false, penalty: '', fFactor: 800 });
    }
  };

  // ==========================================================================
  // POINTS DOWNLOAD MODALS
  // ==========================================================================
  const [fisPointsOpen, setFisPointsOpen] = useState(false);
  const [ussaPointsOpen, setUssaPointsOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const handleTemplateSelect = (e) => {
    const templateType = e.target.value;
    setSelectedTemplate(templateType);
    if (!templateType) return;
    downloadTemplateCsv(templateType);
    setSelectedTemplate('');
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  const APP_VERSION = packageJson.version;

  return (
    <div className="combined-form">
      {/* Unified header — logo, title, About/Tools menus */}
      <AppHeader
        version={APP_VERSION}
        onClearAll={handleClearAll}
        onOpenFisPoints={() => setFisPointsOpen(true)}
        onOpenUssaPoints={() => setUssaPointsOpen(true)}
      />

      {/* Secondary bar — darker Zone4 blue with race info badges */}
      <div className="workflow-indicators">
        {raceMode === 'fis-only' ? (
          headerInfo.eventName && (
            <span className="info-badge event-badge">
              📍 {headerInfo.eventName}
            </span>
          )
        ) : (
          selectedEvent && (
            <span className="info-badge event-badge">
              📍 {selectedEvent.name}
            </span>
          )
        )}
        {headerInfo.raceDate && (
          <span className="info-badge event-badge">
            📅 {(() => { const [,m,d] = headerInfo.raceDate.split('-'); const mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+m-1]; return `${mo} ${+d}`; })()}
          </span>
        )}
        <span className={fisWorkflowEnabled ? 'enabled' : 'disabled'}>
          <img src={fisLogo} alt="FIS" className="inline-fis-icon" /> FIS: {hasFisCodex ? headerInfo.fisCodex : (raceMode === 'fis-only' ? 'FIS Only Mode' : 'No codex')}
        </span>
        {raceMode !== 'fis-only' && (
          <span className={ussaWorkflowEnabled ? 'enabled' : 'disabled'}>
            🇺🇸 USSA: {hasUssaCode ? headerInfo.ussaCode : 'No code'}
          </span>
        )}
        {(headerInfo.gender || headerInfo.sex) && (
          <span className="info-badge">
            {(headerInfo.gender || headerInfo.sex) === 'M' ? '👨 Men' : (headerInfo.gender || headerInfo.sex) === 'W' ? '👩 Women' : '👥 Mixed'}
          </span>
        )}
        {headerInfo.discipline && (
          <span className="info-badge">
            {headerInfo.discipline === 'SP' ? '⚡' : '📏'} {headerInfo.discipline === 'SP' ? 'Sprint' : headerInfo.discipline === 'DI' ? 'Distance' : headerInfo.discipline}
          </span>
        )}
        {headerInfo.eventType && (
          <span className="info-badge">
            🏁 {headerInfo.eventType === 'IS' ? 'Interval Start' : 
               headerInfo.eventType === 'MS' ? 'Mass Start' : 
               headerInfo.eventType === 'Qual' ? 'Qualification' : 
               headerInfo.eventType === 'Final' ? 'Final' : 
               headerInfo.eventType === 'Pur' ? 'Pursuit' : 
               headerInfo.eventType === 'Ski' ? 'Skiathlon' :
               headerInfo.eventType}
          </span>
        )}
        {headerInfo.distance && (
          <span className="info-badge">
            📐 {headerInfo.distance}
          </span>
        )}
        {headerInfo.technique && (
          <span className="info-badge">
            {headerInfo.technique === 'FR' ? '🎿 Freestyle' : headerInfo.technique === 'CL' ? '🎿 Classic' : headerInfo.technique}
          </span>
        )}
      </div>

      {/* Main Layout: Left Nav + Content */}
      <div className="combined-form-layout">
        {/* Left Navigation */}
        <nav className="left-nav">
          {/* 1. Event - Select race mode and event/race */}
          <button
            className={activeSection === 'event' ? 'active' : ''}
            onClick={() => setActiveSection('event')}
          >
            <span className="nav-label">
              <span className="nav-icon">📍</span>
              Event
            </span>
            {(selectedEvent || raceMode === 'fis-only') && <span className="check">✓</span>}
          </button>
          
          {/* 2. Header - Race metadata (enabled after event selected) */}
          <button
            className={activeSection === 'header' ? 'active' : ''}
            onClick={() => setActiveSection('header')}
            disabled={!canAccessHeader}
          >
            <span className="nav-label">
              <span className="nav-icon">📋</span>
              Header
            </span>
          </button>
          
          {/* 3. Jury - Jury members (enabled after event selected) */}
          <button
            className={activeSection === 'jury' ? 'active' : ''}
            onClick={() => setActiveSection('jury')}
            disabled={!canAccessJury}
          >
            <span className="nav-label">
              <span className="nav-icon">👥</span>
              Jury
            </span>
          </button>
          
          {/* 4. File - Upload CSV/XML (enabled after event selected) */}
          <button
            className={activeSection === 'file' ? 'active' : ''}
            onClick={() => setActiveSection('file')}
            disabled={!canAccessFile}
          >
            <span className="nav-label">
              <span className="nav-icon">📁</span>
              File
            </span>
            {originalCsv.fileName && <span className="check">✓</span>}
          </button>
          
          {/* 5. FIS - FIS workflow (only shown if FIS codex specified) */}
          {fisWorkflowEnabled && (
            <button
              className={activeSection === 'fis' ? 'active' : ''}
              onClick={() => setActiveSection('fis')}
              disabled={!canAccessData}
            >
              <span className="nav-label">
                <img src={fisLogo} alt="FIS" className="nav-fis-icon" />
                FIS
              </span>
              {fisWorkflowState.exported && <span className="check">✓</span>}
            </button>
          )}
          
          {/* 6. USSA - USSA workflow (only shown if USSA code specified) */}
          {ussaWorkflowEnabled && (
            <button
              className={activeSection === 'ussa' ? 'active' : ''}
              onClick={() => setActiveSection('ussa')}
              disabled={!canAccessData}
            >
              <span className="nav-label">
                <span className="nav-icon">🇺🇸</span>
                USSA
              </span>
              {ussaWorkflowState.exported && <span className="check">✓</span>}
            </button>
          )}
        </nav>

        {/* Main Content Area */}
        <main className="main-content">
          {/* Error Display */}
          {error && (
            <div className="error-banner">
              {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* Section Content */}
          {activeSection === 'event' && (
            <section className="section-event">
              {/* Race Mode Selection */}
              {!raceMode && (
                <div className="mode-selection">
                  <h2>Select Race Type</h2>
                  <div className="mode-buttons">
                    <button 
                      className="mode-btn ussa-mode"
                      onClick={() => handleModeSelect('ussa')}
                    >
                      <span className="mode-icon">🇺🇸</span>
                      <span className="mode-title">USSA Event</span>
                      <span className="mode-desc">Search for US event in USSA system</span>
                    </button>
                    <button 
                      className="mode-btn fis-mode"
                      onClick={() => handleModeSelect('fis-only')}
                    >
                      <span className="mode-icon">
                        <img src={require('../logos/fis.svg').default} alt="FIS" />
                      </span>
                      <span className="mode-title">Non-USA FIS Race</span>
                      <span className="mode-desc">Enter FIS codex manually (e.g., Canadian races)</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* USSA Event Mode */}
              {raceMode === 'ussa' && (
                <>
                  <div className="mode-indicator">
                    <span>🇺🇸 USSA Event Mode</span>
                    <button className="change-mode-btn" onClick={() => setRaceMode(null)}>
                      Change
                    </button>
                  </div>
                  
                  <h2>Select Event</h2>
                  <EventPicker
                    selectedEvent={selectedEvent}
                    onSelect={handleEventSelect}
                    season={currentSeason}
                    sector={headerInfo.sector}
                  />
                  
                  {selectedEvent && (
                    <>
                      <h3>Select Race</h3>
                      <RacePicker
                        event={selectedEvent}
                        selectedRace={selectedRace}
                        onSelect={handleRaceSelect}
                        categoryProgress={categoryProgress}
                      />
                    </>
                  )}
                </>
              )}
              
              {/* FIS-Only Mode */}
              {raceMode === 'fis-only' && (
                <div className="fis-only-info">
                  <div className="mode-indicator">
                    <span><img src={fisLogo} alt="FIS" className="inline-fis-icon" /> Non-USA FIS Race Mode</span>
                    <button className="change-mode-btn" onClick={() => setRaceMode(null)}>
                      Change
                    </button>
                  </div>
                  <p>Enter your FIS codex manually in the Header section.</p>
                  <button 
                    className="proceed-btn"
                    onClick={() => setActiveSection('header')}
                  >
                    Proceed to Header →
                  </button>
                </div>
              )}
            </section>
          )}

          {activeSection === 'file' && (
            <section className="section-file">
              <div className="section-title-row">
                <h2>Upload Race File</h2>
                <div className="template-download-group">
                  <label htmlFor="template-select">Download Template</label>
                  <select
                    id="template-select"
                    value={selectedTemplate}
                    onChange={handleTemplateSelect}
                  >
                    <option value="">Select...</option>
                    <option value="individual">Download Example CSV Format(Individual Race / Start List)</option>
                    <option value="sprint-final">Download Example CSV Format (Sprint Final)</option>
                    <option value="team">Download Example CSV Format (Team Event)</option>
                  </select>
                </div>
              </div>
              <FileUploader
                currentFile={originalCsv.fileName}
                categoryCount={originalCsv.categories?.length || 0}
                categoryMeta={originalCsv.categoryMeta}
                rawRows={originalCsv.rawRows}
                onUpload={handleFileUpload}
              />
              
              {originalCsv.categories.length > 0 && (
                <>
                  <h3>Select Category</h3>
                  <CategoryPicker
                    categories={originalCsv.categories}
                    categoryMeta={originalCsv.categoryMeta}
                    rawRows={originalCsv.rawRows}
                    selectedCategory={selectedCategory}
                    onSelect={handleCategorySelect}
                    progress={categoryProgress}
                  />
                </>
              )}
            </section>
          )}

          {activeSection === 'header' && (
            <section className="section-header">
              <h2>Header Information</h2>
              <HeaderSummary
                headerInfo={headerInfo}
                setHeaderInfo={setHeaderInfo}
                fisEnabled={fisWorkflowEnabled}
                ussaEnabled={ussaWorkflowEnabled}
                raceMode={raceMode}
              />
            </section>
          )}

          {activeSection === 'jury' && (
            <section className="section-jury">
              <h2>Jury Members</h2>
              <JuryForm
                jury={jury}
                setJury={setJury}
                fisEnabled={fisWorkflowEnabled}
                ussaEnabled={ussaWorkflowEnabled}
              />
            </section>
          )}

          {activeSection === 'fis' && (
            <section className="section-fis">
              <div className="data-section-header">
                <h2>
                  <img src={fisLogo} alt="FIS" className="section-title-icon" />
                  FIS Data
                  {selectedCategory && <span className="category-badge">{selectedCategory}</span>}
                  {fisRacers.length > 0 && <span className="racer-count-badge">{fisRacers.length} racers</span>}
                </h2>
              </div>
              
              {fisRacers.length > 0 ? (
                <DataWorkflow
                  type="fis"
                  racers={fisRacers}
                  setRacers={setFisRacers}
                  headerInfo={headerInfo}
                  jury={jury}
                  workflowState={fisWorkflowState}
                  setWorkflowState={setFisWorkflowState}
                  onClean={handleCleanData}
                  onCalculatePoints={handleCalculatePoints}
                  onDownloadXML={handleDownloadXML}
                />
              ) : (
                <p className="no-data">
                  {originalCsv.fileName
                    ? originalCsv.categories.length > 0 
                      ? 'Select a category from the File section to view racers'
                      : 'No racer data found in file'
                    : 'Upload a file in the File section to see racer data'}
                </p>
              )}
            </section>
          )}

          {activeSection === 'ussa' && (
            <section className="section-ussa">
              <div className="data-section-header">
                <h2>
                  <span className="section-title-icon">🇺🇸</span>
                  USSA Data
                  {selectedCategory && <span className="category-badge">{selectedCategory}</span>}
                  {ussaRacers.length > 0 && <span className="racer-count-badge">{ussaRacers.length} racers</span>}
                </h2>
              </div>
              
              {ussaRacers.length > 0 ? (
                <DataWorkflow
                  type="ussa"
                  racers={ussaRacers}
                  setRacers={setUssaRacers}
                  headerInfo={headerInfo}
                  jury={jury}
                  workflowState={ussaWorkflowState}
                  setWorkflowState={setUssaWorkflowState}
                  onClean={handleCleanData}
                  onCalculatePoints={handleCalculatePoints}
                  onDownloadXML={handleDownloadXML}
                />
              ) : (
                <p className="no-data">
                  {originalCsv.fileName
                    ? originalCsv.categories.length > 0 
                      ? 'Select a category from the File section to view racers'
                      : 'No racer data found in file'
                    : 'Upload a file in the File section to see racer data'}
                </p>
              )}
            </section>
          )}

        </main>
      </div>
      
      {/* Clean Preview Modal */}
      <CleanPreview
        isOpen={cleanPreviewOpen}
        onClose={() => setCleanPreviewOpen(false)}
        originalRacers={cleanPreviewType === 'fis' ? fisRacers : ussaRacers}
        cleanupResult={cleanPreviewResult}
        type={cleanPreviewType}
        onAccept={handleAcceptCleanup}
        onDecline={handleDeclineCleanup}
      />
      
      {/* Column Mapper Modal - for when CSV has unrecognized column names */}
      {columnMapperOpen && (
        <ColumnMapperModal
          missingFields={columnMapperData.missingFields}
          availableColumns={columnMapperData.availableColumns}
          onConfirm={handleColumnMappingConfirm}
          onCancel={handleColumnMappingCancel}
        />
      )}

      {/* Points download modals (from Tools menu) */}
      <FisPointsModal isOpen={fisPointsOpen} onClose={() => setFisPointsOpen(false)} raceDate={headerInfo.raceDate} />
      <UssaPointsModal
        isOpen={ussaPointsOpen}
        onClose={() => setUssaPointsOpen(false)}
        raceDate={headerInfo.raceDate}
      />
    </div>
  );
}

export default CombinedForm;
